import { API_BASE_URL } from '../constants';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

const request = async <T>(method: HttpMethod, endpoint: string, body?: any): Promise<T> => {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
        method,
        headers,
        credentials: 'include',
    };

    if (body) {
        config.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    // Handle empty-body responses first (like 204 No Content).
    if (response.status === 204) {
        return null as T;
    }

    const responseText = await response.text();

    if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (responseText) {
            try {
                const errorData = JSON.parse(responseText);
                errorMessage = errorData.error || responseText;
            } catch (e) {
                errorMessage = responseText;
            }
        }
        throw new Error(errorMessage);
    }

    // If there's no body content, return null.
    if (!responseText) {
        return null as T;
    }

    // Only attempt to parse if the server indicates it's sending JSON.
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        try {
            return JSON.parse(responseText) as T;
        } catch (e) {
            console.error("Failed to parse JSON response:", responseText);
            throw new Error('Failed to parse a JSON response from the server.');
        }
    }

    // For successful responses that are not JSON (e.g., '201 Created' with plain text),
    // we can either return the text or null. Returning null is safer for generic handling.
    return null as T;
};

export const api = {
    get: <T>(endpoint: string) => request<T>('GET', endpoint),
    post: <T>(endpoint: string, body: any) => request<T>('POST', endpoint, body),
    put: <T>(endpoint: string, body: any) => request<T>('PUT', endpoint, body),
    delete: <T>(endpoint: string) => request<T>('DELETE', endpoint),
};